import { DataView } from 'primereact/dataview';
import { SelectButton } from 'primereact/selectbutton';
import { useEffect, useState } from 'react';
import PageWrapper from '../../../../components/Display/PageWrapper';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useNavigationContext } from '../../../../contexts/NavigationContext';
import { privateFetch } from '../../../../utils/apiHelper';
import { CONSTANT } from '../../../../utils/constants';
import HighlightDetail from './HighlightDetail';
import HighlightItem from './HighlightItem';
import HighlightManager from './HighlightManager';
import DocumentPanel from '../../common/document/DocumentPanel';

function HighlightPage({ isActive }) {
  const { accountContext } = useAccountContext();
  const { openQBHighlightDetail, navigationContext } = useNavigationContext();
  const [highlightList, setHighlightList] = useState([]);
  const [highlightDetail, setHighlightDetail] = useState(undefined);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('highlight');
  const [duplicationMode, setDuplicationMode] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(undefined);
  const [documentManagerIsOpen, setDocumentManagerIsOpen] = useState(false);

  useEffect(() => {
    fetchHighlightList();
  }, [typeFilter]);

  const fetchHighlightList = async () => {
    await privateFetch(
      'GET',
      'quatrebis',
      `/cu/${accountContext.id}/highlight/?type=${typeFilter || ''}`
    ).then((res) => {
      setHighlightList(res);
    });
  };

  useEffect(() => {
    fetchHighlightList();
  }, [navigationContext]);

  const fetchHighlightDetail = async () => {
    await privateFetch(
      'GET',
      'quatrebis',
      `/cu/${accountContext.id}/highlight/${navigationContext.acitveHighlightId}/`
    ).then((res) => {
      if (res) setHighlightDetail(res);
    });
  };

  const handleClickHighlight = (highlight) => {
    openQBHighlightDetail(highlight);
  };

  useEffect(() => {
    if (panelIsOpen === false) {
      setDuplicationMode(false) && setHighlightDetail(undefined);
    }
  }, [panelIsOpen]);

  const handleDuplicateHighlight = (highlight) => {
    handleOpenManager();
    setDuplicationMode(true);
    handleClickHighlight(highlight);
  };

  useEffect(() => {
    if (navigationContext.acitveHighlightId) {
      fetchHighlightDetail();
    } else setHighlightDetail(undefined);
  }, [navigationContext]);

  const goBackHighlightAction = {
    onClick: handleClickHighlight,
    label: 'Retour à la liste',
    icon: 'pi pi-arrow-left'
  };

  const handleOpenManager = () => {
    setPanelIsOpen(true);
  };

  const newHighlightAction = {
    onClick: handleOpenManager,
    label: 'Ajouter un temps fort / Alumni',
    icon: 'pi-plus'
  };

  const createHighlight = async (highlightForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      'quatrebis',
      `/cu/${accountContext.id}/highlight/`,
      'Le temps fort / Alumni a bien été ajouté',
      JSON.stringify(highlightForm)
    ).then(() => {
      fetchHighlightList();
      setPanelIsOpen(false);
      setLoading(false);
      setDuplicationMode(false);
      setHighlightDetail(undefined);
    });
  };

  const updateHighlight = async (highlightForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'quatrebis',
      `/cu/${accountContext.id}/highlight/${highlightDetail.id}/`,
      'Le temps fort / Alumni a bien été modifié',
      JSON.stringify(highlightForm)
    ).then(() => {
      fetchHighlightList();
      setPanelIsOpen(false);
      fetchHighlightDetail(highlightDetail.id);
      setLoading(false);
    });
  };

  const deleteHighlight = async (highlight) => {
    await privateFetch(
      'DELETE',
      'quatrebis',
      `/cu/${accountContext.id}/highlight/${highlight.id}/`,
      'Le temps fort / Alumni a bien été supprimé'
    ).then(() => {
      fetchHighlightList();
      setHighlightDetail(undefined);
    });
  };

  const handleCreateDocument = () => {
    setSelectedDocument(undefined);
    setDocumentManagerIsOpen(true);
  };

  const handleUpdateDocument = (document) => {
    setSelectedDocument(document);
    setDocumentManagerIsOpen(true);
  };

  const uploadHighlightDocument = async (documentForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      'quatrebis',
      `/cu/${accountContext.id}/highlight/${highlightDetail.id}/file/`,
      'Le fichier a bien été enregistrée',
      documentForm,
      false,
      true
    ).then(() => {
      fetchHighlightDetail();
      setLoading(false);
      setDocumentManagerIsOpen(false);
    });
  };

  const updateDocument = async (documentForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'resources',
      `/cu/${accountContext.id}/file/${selectedDocument.id}/`,
      'Le document a bien été modifiée',
      documentForm,
      false,
      true
    ).then(() => {
      fetchHighlightDetail();
      setLoading(false);
      setDocumentManagerIsOpen(false);
    });
  };

  const handleDeleteDocument = async (document) => {
    await privateFetch(
      'DELETE',
      'resources',
      `/cu/${accountContext.id}/file/${document.id}/`,
      'Le document a bien été supprimée'
    ).then(() => {
      fetchHighlightDetail();
      setSelectedDocument(undefined);
    });
  };

  const itemTemplate = (highlight) => (
    <HighlightItem
      highlight={highlight}
      handleClickHighlight={() => handleClickHighlight(highlight)}
      handleDuplicateHighlight={() => handleDuplicateHighlight(highlight)}
    />
  );

  const highlightFilters = (
    <div className="flex flex-row flex-wrap gap-2 justify-start">
      <div className="flex flex-row flex-wrap gap-2 ">
        <div className="fit-content mb-4">
          <SelectButton
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.value)}
            options={CONSTANT.quatrebis.highlightType}
            className="rounded-full"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
      <PageWrapper
        label="Temps forts / Alumni"
        labelAction={handleClickHighlight}
        action={highlightDetail ? goBackHighlightAction : newHighlightAction}
        detail={highlightDetail}>
        {highlightDetail && !duplicationMode ? (
          <HighlightDetail
            highlight={highlightDetail}
            handleUpdateHighlight={handleOpenManager}
            handleDelete={deleteHighlight}
            handleCreateFile={handleCreateDocument}
            handleUpdateFile={handleUpdateDocument}
            handleDeleteFile={handleDeleteDocument}
          />
        ) : (
          <>
            {highlightFilters}
            <DataView
              value={highlightList}
              itemTemplate={itemTemplate}
              className="pb-20"
              emptyMessage="Aucun temps fort / Alumni trouvé"
            />
          </>
        )}
      </PageWrapper>
      <HighlightManager
        isOpen={panelIsOpen}
        onClose={() => {
          setDuplicationMode(false);
          setPanelIsOpen(false);
        }}
        highlight={highlightDetail}
        onCreate={createHighlight}
        onUpdate={updateHighlight}
        loading={loading}
        duplicationMode={duplicationMode}
        setPanelIsOpen={setPanelIsOpen}
        setHighlightDetail={setHighlightDetail}
      />
      <DocumentPanel
        document={selectedDocument}
        loading={loading}
        isOpen={documentManagerIsOpen}
        onClose={() => {
          setSelectedDocument(null);
          setDocumentManagerIsOpen(false);
        }}
        onCreate={uploadHighlightDocument}
        onUpdate={updateDocument}
      />
    </div>
  );
}

export default HighlightPage;
